/**
 * AI Generatedc environment detection utility
 *
 * URL patterns:
 * - local:         {lms}.local.courseware.cpat.com
 * - development:   {lms}.development.courseware.cpat.com
 * - alpha:         {lms}.development.alpha.courseware.cpat.com
 * - beta:          {lms}.beta.courseware.cpat.com
 * - qa-release:    {lms}.qa-release.courseware.cpat.com
 * - qa-release-02: {lms}.qa-release-02.courseware.cpat.com
 * - production:    {lms}.courseware.cpat.com (or unknown)
 */

export type Environment = "local" | "development" | "alpha" | "beta" | "qa-release" | "qa-release-02" | "production";

// Friendly aliases for the environment names
export type EnvironmentAlias = Environment | "qar" | "qar2";

const ENVIRONMENT_ALIAS_MAP: Record<string, Environment> = {
  qar: "qa-release",
  qar2: "qa-release-02",
};

function normalizeEnvironment(env: EnvironmentAlias): Environment {
  return ENVIRONMENT_ALIAS_MAP[env] ?? (env as Environment);
}

/**
 * Detects the current environment based on the hostname
 */
export function getCurrentEnvironment(): Environment {
  const hostname = globalThis.location.hostname;

  // Local development
  if (hostname.includes(".local.")) return "local";

  // Alpha environment: development.alpha in the hostname
  if (hostname.includes(".development.alpha.")) return "alpha";

  // Development environment
  if (hostname.includes(".development.")) return "development";

  // Beta environment
  if (hostname.includes(".beta.")) return "beta";

  // QA Release 02 environment (check this before qa-release to avoid partial match)
  if (hostname.includes(".qa-release-02.")) return "qa-release-02";

  // QA Release environment
  if (hostname.includes(".qa-release.")) return "qa-release";

  // Production or unknown
  return "production";
}

/**
 * Check if the current environment matches any of the provided environments
 * @param envs - One or more environments to check against (supports aliases like 'qar' and 'qar2')
 * @returns true if current environment matches any of the provided environments
 *
 * @example
 * // Show feature only in development and alpha
 * if (isEnvironment('development', 'alpha')) {
 *   showDevFeature();
 * }
 *
 * @example
 * // Show feature in QA environments (using aliases)
 * if (isEnvironment('qar', 'qar2')) {
 *   showQaFeature();
 * }
 */
export function isEnvironment(...envs: EnvironmentAlias[]): boolean {
  const currentEnv = getCurrentEnvironment();
  return envs.map(normalizeEnvironment).includes(currentEnv);
}

/**
 * Check if the current environment is NOT any of the provided environments
 * @param envs - One or more environments to exclude
 * @returns true if current environment does NOT match any of the provided environments
 *
 * @example
 * // Hide feature in production
 * if (isNotEnvironment('production')) {
 *   showNonProdFeature();
 * }
 */
export function isNotEnvironment(...envs: EnvironmentAlias[]): boolean {
  return !isEnvironment(...envs);
}

/**
 * Check if we're in any non-production environment
 */
export function isDevelopmentEnvironment(): boolean {
  return isNotEnvironment("production");
}

/**
 * Check if we're in production
 */
export function isProductionEnvironment(): boolean {
  return isEnvironment("production");
}

/**
 * Get the LMS domain (first segment of hostname)
 * @example "cpattest" from "cpattest.development.courseware.cpat.com"
 */
export function getLmsDomain(): string {
  return globalThis.location.hostname?.split(".")?.at(0) ?? "cpattest";
}
